import React from 'react';
import { Card, Row, Col } from 'antd';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { CheckCircleOutlined } from '@ant-design/icons';
import { RequestTrackingStatisticsResponse } from '@openapi';

interface ChartsSectionProps {
  statistics: RequestTrackingStatisticsResponse;
  errorDistribution: Record<string, number>;
}

const ChartsSection: React.FC<ChartsSectionProps> = ({ statistics, errorDistribution }) => {
  const getChartData = () => {
    if (!statistics || !statistics.versionStats)
      return { categories: [], avgTimes: [], successCounts: [], errorCounts: [] };

    const categories = statistics.versionStats.map((v) => v.apiVersion || 'unknown');
    const avgTimes = statistics.versionStats.map((v) => Number((v.avgExecutionTime || 0).toFixed(2)));
    const successCounts = statistics.versionStats.map((v) => v.successCount || 0);
    const errorCounts = statistics.versionStats.map((v) => v.errorCount || 0);

    return {
      categories,
      avgTimes,
      successCounts,
      errorCounts,
    };
  };

  const chartData = getChartData();

  const responseTimeOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: true,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val}ms`,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
    },
    xaxis: {
      categories: chartData.categories,
      title: {
        text: 'API Version',
      },
    },
    yaxis: {
      title: {
        text: 'Average Response Time (ms)',
      },
    },
    colors: ['#1890ff'],
    title: {
      text: 'Average Response Time by API Version',
      align: 'left',
    },
  };

  const responseTimeSeries = [
    {
      name: 'Avg Response Time',
      data: chartData.avgTimes,
    },
  ];

  const successErrorOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: {
        show: true,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 8,
      },
    },
    xaxis: {
      categories: chartData.categories,
      title: {
        text: 'API Version',
      },
    },
    yaxis: {
      title: {
        text: 'Request Count',
      },
    },
    legend: {
      position: 'top',
    },
    colors: ['#52c41a', '#ff4d4f'],
    title: {
      text: 'Success vs Error Requests',
      align: 'left',
    },
  };

  const successErrorSeries = [
    {
      name: 'Success',
      data: chartData.successCounts,
    },
    {
      name: 'Errors',
      data: chartData.errorCounts,
    },
  ];

  const getErrorStatusData = () => {
    if (Object.keys(errorDistribution).length === 0) return { labels: [], counts: [] };

    const sortedEntries = Object.entries(errorDistribution)
      .map(([status, count]) => [parseInt(status), count] as [number, number])
      .sort((a, b) => b[1] - a[1]); // Sort by count descending

    const labels = sortedEntries.map(([status]) => `${status}`);
    const counts = sortedEntries.map(([, count]) => count);

    return { labels, counts };
  };

  const errorStatusData = getErrorStatusData();

  const errorStatusOptions: ApexOptions = {
    chart: {
      type: 'pie',
      height: 350,
    },
    labels: errorStatusData.labels,
    colors: ['#ff4d4f', '#ff7875', '#ffa39e', '#ffccc7', '#ffd8bf'],
    title: {
      text: 'Error Status Code Distribution',
      align: 'left',
    },
    legend: {
      position: 'bottom',
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} requests`,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number, opts: { seriesIndex: number; w: { config: { labels: string[] } } }) => {
        return `${opts.w.config.labels[opts.seriesIndex]}: ${Math.round(val)}%`;
      },
    },
  };

  const errorStatusSeries = errorStatusData.counts;

  return (
    <Row gutter={16} style={{ marginBottom: 24 }}>
      <Col xs={24} lg={8}>
        <Card>
          <ReactApexChart options={responseTimeOptions} series={responseTimeSeries} type="bar" height={350} />
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card>
          <ReactApexChart options={successErrorOptions} series={successErrorSeries} type="bar" height={350} />
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card style={{ height: '100%' }}>
          {errorStatusData.counts.length > 0 ? (
            <ReactApexChart options={errorStatusOptions} series={errorStatusSeries} type="pie" height={350} />
          ) : (
            <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
              <CheckCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <div>No errors in this time range</div>
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default ChartsSection;
